import axios from "axios";
import { ArrowUpDown, Eye, Loader2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { DataTable } from "../../../components/DataTable";
import LoaderPage from "../../../components/LoaderPage";
import UploadImage from "../../../components/UploadImage";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Card, CardContent } from "../../../components/ui/card";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "../../../components/ui/drawer";
import { Input } from "../../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { useLanguage } from "../../../context/LanguageContext";
import { BaseUrlApi, ErrorMessage } from "../../../lib/api";
import { CategoriesData } from "../../../lib/languages";
import { useNavigate } from "react-router-dom";
import EditorTextArea from "../../../components/EditorTextArea";
import DrawerAddCategory from "./DrawerAddCategory";
import DialogDeleteCategory from "./DialogDeleteCategory";
import { useAPI } from "../../../context/APIContext";

export default function AdminCategories() {
  const { categoriesItems } = useAPI();
  const { categories, loadingCategories, getAllCategories } = categoriesItems;
  
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = CategoriesData[language] || CategoriesData["en"];

  const columns = [
    {
      accessorKey: `title.en`,
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="p-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          {t.titleEn}
          <ArrowUpDown className="h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => row.original.title?.en || "---",
    },
    {
      accessorKey: `title.ar`,
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="p-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          {t.titleAr}
          <ArrowUpDown className="h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => row.original.title?.ar || "---",
    },
    {
      accessorKey: "created_by.name",
      header: t.createdBy,
    },
    {
      accessorKey: "sub_category_count",
      header: t.subCategoryCount,
    },
    {
      accessorKey: "status",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="p-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          {t.status}
          <ArrowUpDown className="h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <Badge variant={row.getValue("status") ? "default" : "destructive"}>
          {row.getValue("status") ? t.active : t.inactive}
        </Badge>
      ),
    },
    {
      id: "actions",
      header: t.actions,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Button
            size="icon"
            className="rounded-xl h-9"
            onClick={() => navigate(`/admin/category-view/${row.original.id}`)}
          >
            <Eye />
          </Button>
          <DialogDeleteCategory
            item={row.original}
            getAllCategories={getAllCategories}
          />
        </div>
      ),
    },
  ];

  const ActiveCategories = categories.filter((item) => item.status === 1);
  const InActiveCategories = categories.filter((item) => item.status === 0);

  if (loadingCategories) {
    return <LoaderPage title={t.loadingCategories} />;
  }

  return (
    <div className="space-y-10" dir={language === "ar" ? "rtl" : "ltr"}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <CategoryCard
          title={t.allCategories}
          count={categories.length || 0}
          svg={
            <svg
              width="25"
              height="25"
              viewBox="0 0 25 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                opacity="0.5"
                d="M9.83398 10.5H15.834V22.5H9.83398V10.5ZM2.83398 10.5V21.5C2.83398 21.7652 2.93934 22.0196 3.12688 22.2071C3.31441 22.3946 3.56877 22.5 3.83398 22.5H7.83398V10.5H2.83398Z"
                fill="#2155CD"
              />
              <path
                opacity="0.25"
                d="M22.834 8.5H2.83398V10.5H7.83398V22.5H9.83398V10.5H15.834V22.5H17.834V10.5H22.834V8.5Z"
                fill="#2155CD"
              />
              <path
                opacity="0.5"
                d="M17.834 10.5V22.5H21.834C22.0992 22.5 22.3536 22.3946 22.5411 22.2071C22.7286 22.0196 22.834 21.7652 22.834 21.5V10.5H17.834Z"
                fill="#2155CD"
              />
              <path
                d="M3.83398 2.5H21.834C22.0992 2.5 22.3536 2.60536 22.5411 2.79289C22.7286 2.98043 22.834 3.23478 22.834 3.5V8.5H2.83398V3.5C2.83398 3.23478 2.93934 2.98043 3.12688 2.79289C3.31441 2.60536 3.56877 2.5 3.83398 2.5Z"
                fill="#2155CD"
              />
            </svg>
          }
        />
        <CategoryCard
          title={t.activeCategories}
          count={ActiveCategories.length || 0}
          svg={
            <svg
              width="25"
              height="25"
              viewBox="0 0 25 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.1 16.64L15.7333 12.47C16 12.23 16.3222 12.11 16.7 12.11C17.0778 12.11 17.4 12.23 17.6667 12.47C17.9333 12.71 18.0667 13 18.0667 13.34C18.0667 13.68 17.9333 13.97 17.6667 14.21L12.0333 19.28C11.7667 19.52 11.4556 19.64 11.1 19.64C10.7444 19.64 10.4333 19.52 10.1667 19.28L7.33333 16.73C7.06667 16.49 6.93333 16.2 6.93333 15.86C6.93333 15.52 7.06667 15.23 7.33333 14.99C7.6 14.75 7.92222 14.63 8.3 14.63C8.67778 14.63 9 14.75 9.26667 14.99L11.1 16.64ZM3.16667 24.5C2.43333 24.5 1.80578 24.2652 1.284 23.7956C0.762222 23.326 0.500889 22.7608 0.5 22.1V5.3C0.5 4.64 0.761333 4.0752 1.284 3.6056C1.80667 3.136 2.43422 2.9008 3.16667 2.9H4.5V1.7C4.5 1.36 4.628 1.0752 4.884 0.845604C5.14 0.616004 5.45644 0.500804 5.83333 0.500004C6.21022 0.499204 6.52711 0.614404 6.784 0.845604C7.04089 1.0768 7.16844 1.3616 7.16667 1.7V2.9H17.8333V1.7C17.8333 1.36 17.9613 1.0752 18.2173 0.845604C18.4733 0.616004 18.7898 0.500804 19.1667 0.500004C19.5436 0.499204 19.8604 0.614404 20.1173 0.845604C20.3742 1.0768 20.5018 1.3616 20.5 1.7V2.9H21.8333C22.5667 2.9 23.1947 3.1352 23.7173 3.6056C24.24 4.076 24.5009 4.6408 24.5 5.3V22.1C24.5 22.76 24.2391 23.3252 23.7173 23.7956C23.1956 24.266 22.5676 24.5008 21.8333 24.5H3.16667ZM3.16667 22.1H21.8333V10.1H3.16667V22.1Z"
                fill="#2155CD"
              />
            </svg>
          }
        />
        <CategoryCard
          title={t.inactiveCategories}
          count={InActiveCategories.length || 0}
          svg={
            <svg
              width="25"
              height="25"
              viewBox="0 0 25 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M21.1422 23.8L18.8422 21.5H16.9922V19.65L14.9922 17.65V21.5H10.3422V16.825H14.1672L12.1672 14.825H10.3422V12.975L8.34219 10.975V14.825H3.66719V10.175H7.54219L5.54219 8.17501H3.66719V6.30001L1.36719 4.00001L2.76719 2.60001L22.5672 22.4L21.1422 23.8ZM5.66719 21.5C5.11719 21.5 4.64652 21.3043 4.25519 20.913C3.86385 20.5217 3.66785 20.0507 3.66719 19.5V16.825H8.34219V21.5H5.66719ZM21.6672 18.6L19.8922 16.825H21.6672V18.6ZM17.8922 14.825L16.9922 13.925V10.175H21.6672V14.825H17.8922ZM14.9922 11.925L13.2422 10.175H14.9922V11.925ZM11.2422 8.17501L10.3422 7.27501V3.50001H14.9922V8.17501H11.2422ZM16.9922 8.17501V3.50001H19.6672C20.2172 3.50001 20.6882 3.69601 21.0802 4.08801C21.4722 4.48001 21.6679 4.95067 21.6672 5.50001V8.17501H16.9922ZM8.34219 5.27501L6.56719 3.50001H8.34219V5.27501Z"
                fill="#2155CD"
              />
            </svg>
          }
        />
      </div>
      <div>
        <div className="flex items-center justify-between gap-2">
          <h1 className="text-2xl font-bold">{t.categories}</h1>
          <DrawerAddCategory getAllCategories={getAllCategories} />
        </div>
        <DataTable
          columns={columns}
          data={categories}
          title={t.categoriesByTitle}
          filterNames={[`title_ar`, `title_en`]}
        />
      </div>
    </div>
  );
}

function CategoryCard({ title, count, svg }) {
  return (
    <Card className="overflow-hidden border-2 border-blue-600">
      <CardContent className="px-5 py-8 flex flex-col items-center justify-center gap-2">
        {svg}
        <p className="font-bold text-xl">{title}</p>
        <div className="font-bold text-2xl text-blue-600">{count}</div>
      </CardContent>
    </Card>
  );
}

const DrawerUpdateCategory = ({ item, getAllCategories }) => {
  const { language } = useLanguage();
  const t = CategoriesData[language] || CategoriesData["en"];

  const [editMode, setEditMode] = useState(false);
  const [open, setOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState(item?.image);
  const [form, setForm] = useState({
    title_en: item?.title?.en,
    title_ar: item?.title?.ar,
    description_en: item?.description?.en,
    description_ar: item?.description?.ar,
    status: item.status,
  });

  const handleEditCategory = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData();
      if (form.title_ar || form.title_en) {
        formData.append(
          "title",
          JSON.stringify({ ar: form.title_ar, en: form.title_en })
        );
      }
      if (form.description_ar || form.description_en) {
        formData.append(
          "description",
          JSON.stringify({ ar: form.description_ar, en: form.description_en })
        );
      }
      if (image) formData.append("image", image || null);
      formData.append("status", form.status ? 1 : 0);

      await axios.post(`${BaseUrlApi}/categories/${item.id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast(t.categoryUpdated, {
        description: t.categoryUpdatedDescription,
      });

      await getAllCategories();
      setEditMode(false);
      setOpen(false);
    } catch (error) {
      setErrors(ErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const Categories = [
    {
      id: 1,
      title: {
        en: "Address",
        ar: "العنوان",
      },
      created_by: {
        id: 1,
        name: "Almadbwly",
      },
      status: 0,
      course_count: 0,
    },
    {
      id: 2,
      title: {
        en: "React",
        ar: "ريأكت",
      },
      created_by: {
        id: 2,
        name: "Khamis",
      },
      status: 1,
      course_count: 12,
    },
  ];

  const columns = [
    {
      accessorKey: `title.en`,
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            className="p-0 hover:bg-transparent"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t.titleEn}
            <ArrowUpDown className="h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const title = row.original.title;
        return title?.en || "---";
      },
    },
    {
      accessorKey: `title.ar`,
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            className="p-0 hover:bg-transparent"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t.titleAr}
            <ArrowUpDown className="h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const title = row.original.title;
        return title?.ar || "---";
      },
    },
    {
      accessorKey: "created_by.name",
      header: t.createdBy,
    },
    {
      accessorKey: "sub_category_count",
      header: t.subCategoryCount,
    },
    {
      accessorKey: "status",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            className="p-0 hover:bg-transparent"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t.status}
            <ArrowUpDown className="h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const status = row.getValue("status");
        return (
          <Badge variant={status ? "default" : "destructive"}>
            {status ? t.active : t.inactive}
          </Badge>
        );
      },
    },
    {
      id: "actions",
      header: t.actions,
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="flex items-center gap-2">
            {/* <DrawerUpdateCategory
              item={item}
              getAllCategories={getAllCategories}
            /> */}
            <DialogDeleteCategory
              item={item}
              getAllCategories={getAllCategories}
            />
          </div>
        );
      },
    },
  ];

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <Button
          size="icon"
          className="rounded-xl h-9"
          onClick={() => setEditMode(false)}
        >
          <Eye />
        </Button>
      </DrawerTrigger>
      <DrawerContent dir={language === "ar" ? "rtl" : "ltr"}>
        <div className="h-[calc(100vh-2rem)] max-w-7xl mx-auto overflow-auto hide-scrollbar">
          <DrawerHeader
            className={"flex items-center justify-between flex-wrap gap-4"}
          >
            <div className="w-full sm:w-fit">
              <DrawerTitle className="font-bold text-2xl">
                {t.editCategory}{" "}
                {item.title[language] ||
                  item.title.en ||
                  item.title.ar ||
                  "---"}
              </DrawerTitle>
              <DrawerDescription className="font-medium text-sm">
                {t.modifyCategory}
              </DrawerDescription>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-fit">
              {editMode ? (
                <Button
                  className="w-full sm:w-fit h-10 bg-blue-600 text-white hover:bg-blue-600/80 rounded-xl"
                  onClick={handleEditCategory}
                  disabled={loading || !editMode}
                >
                  {loading && (
                    <div className="animate-spin">
                      <Loader2 />
                    </div>
                  )}
                  {t.editCategory}
                </Button>
              ) : (
                <Button
                  className="w-full sm:w-fit h-10 bg-green-600 text-white hover:bg-green-600/80 rounded-xl"
                  onClick={() => setEditMode(true)}
                  disabled={loading || editMode}
                >
                  Edit Mode
                </Button>
              )}

              <DrawerClose asChild>
                <Button
                  variant="outline"
                  className="w-full sm:w-fit h-10 border-2 border-blue-600 text-blue-600 hover:text-blue-500 rounded-xl"
                >
                  Close
                </Button>
              </DrawerClose>
            </div>
          </DrawerHeader>

          {/* Inputs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4 relative">
            <div className="col-span-1 space-y-4 md:sticky top-2 self-start">
              {/* Upload Img */}
              <Card>
                <CardContent className="p-4 space-y-4">
                  <p className="font-bold text-xl">Thumbnail</p>
                  <UploadImage
                    image={image}
                    setImage={setImage}
                    disabled={!editMode}
                  />
                  {errors?.image && (
                    <p className="text-red-500 font-semibold text-sm">
                      {errors.image[0]}
                    </p>
                  )}
                  <div className="font-normal text-sm text-muted-foreground text-center">
                    Only images with png or jpg extension can be placed.
                  </div>
                </CardContent>
              </Card>
              {/* Status */}
              <Card>
                <CardContent className="p-4 space-y-4">
                  <div className="flex items-center gap-4 justify-between border-b pb-2">
                    <p className="font-bold text-xl">Status</p>
                    <div
                      className={`size-4 ${
                        !!form?.status ? "bg-green-500" : "bg-red-500"
                      }  rounded-full`}
                    />
                  </div>
                  <div className="grid gap-2">
                    <p className="text-sm">Select status</p>
                    <Select
                      disabled={!editMode}
                      value={!!form?.status}
                      onValueChange={(e) => setForm({ ...form, status: e })}
                    >
                      <SelectTrigger
                        className="rounded-lg sm:ml-auto"
                        aria-label="Select status"
                      >
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent className="rounded-xl">
                        <SelectItem value={true} className="rounded-lg">
                          {t.active}
                        </SelectItem>
                        <SelectItem value={false} className="rounded-lg">
                          {t.inactive}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            </div>
            <div className="col-span-1 lg:col-span-2">
              {/* General information */}
              <Card className="h-full flex flex-col">
                <CardContent className="p-4 space-y-4 flex-1 flex flex-col">
                  <p className="font-bold text-xl border-b pb-2">
                    General information
                  </p>
                  <div className="space-y-4 flex-1 flex flex-col">
                    <div className="flex items-center gap-4">
                      <div className="flex flex-col gap-2 w-full">
                        <label htmlFor="title_en" className="text-sm">
                          {t.titleEn}
                        </label>
                        <Input
                          readOnly={!editMode}
                          id="title_en"
                          name="title_en"
                          type="text"
                          placeholder="Enter title in English"
                          value={form.title_en}
                          onChange={(e) =>
                            setForm({ ...form, title_en: e.target.value })
                          }
                        />
                        {errors?.title && (
                          <p className="text-red-500 font-semibold text-sm">
                            {errors.title[0]}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col gap-2 w-full">
                        <label htmlFor="title_ar" className="text-sm">
                          {t.titleAr}
                        </label>
                        <Input
                          readOnly={!editMode}
                          id="title_ar"
                          name="title_ar"
                          type="text"
                          placeholder="Enter title in Arabic"
                          value={form.title_ar}
                          onChange={(e) =>
                            setForm({ ...form, title_ar: e.target.value })
                          }
                        />
                        {errors?.title && (
                          <p className="text-red-500 font-semibold text-sm">
                            {errors.title[0]}
                          </p>
                        )}
                      </div>
                    </div>
                    {/* Description */}
                    <div className="flex flex-col gap-2 flex-1">
                      <label htmlFor="description" className="text-sm">
                        {t.descriptionAr} & {t.descriptionEn}
                      </label>
                      <EditorTextArea
                        form={form}
                        setForm={setForm}
                        disabled={!editMode}
                        en={"description_en"}
                        ar={"description_ar"}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Sub Categories */}
          <DataTable
            columns={columns}
            data={Categories}
            title={t.categoriesByTitle}
            filterNames={[`title_ar`, `title_en`]}
          />
        </div>
      </DrawerContent>
    </Drawer>
  );
};
